import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import SubstanceMetaItemComponent from '@Components/Results/substances/substance-meta-item.component';
import { SUBSTANCE_DETAILS_TOOLTIPS } from '@Constants/ElementConstants';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import CasNumberContainer, { isCasNumberAllowed } from '@Containers/CasNumber';
import { Icon } from '@els/els-react--icon';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import store from '@Store';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

import './SubstanceDetailsContainer.scss';

function SubstanceHeader({
  substance: { sourceUrl, details, molImage, similarityPercentage },
  database: { id: databaseId, name: databaseName, isCIDatabase },
  onToggle,
  metadata,
}) {
  const resultName = get(metadata, 'resultName');
  const chemicalNames = details?.chemicalNames ? details.chemicalNames : [];
  const name = isArray(chemicalNames) && chemicalNames.length > 0 ? chemicalNames[0] : `${databaseName} ID: ${details?.id || ''}`;
  const mapped = SUBSTANCE_DETAILS_TOOLTIPS[databaseId];
  const tooltip = get(mapped, 'id') || `${databaseName} ID`;
  const { sort } = getCombinedRouteParams(store.getState());

  return (
    <header className="u-els-margin-bottom">
      <h2 className="substance__title u-els-margin-bottom-1o2 u-els-font-size-base u-els-line-height-default u-els-font-family-bold">
        <InnerHTML
          mode="escapeCustom"
          tag="a"
          className="u-els-color-n8"
          onClick={(e) => {
            e.preventDefault();
            onToggle();
          }}
          html={name}
        />
      </h2>
      <div className="substance__meta u-els-display-flex u-els-font-size-meta">
        {details?.formula && <SubstanceMetaItemComponent tooltip="Linear Structure Formula" content={details.linearFormula} />}
        {details?.weight && <SubstanceMetaItemComponent tooltip="Molecular Weight" content={details.weight} />}
        {details?.id && !isCIDatabase && <SubstanceMetaItemComponent link={{ sourceUrl }} tooltip={tooltip} content={details.id} />}
        {!Auth.isFeatureEnabled(FEATURES.disableCasNumberCta) ? (
          <CasNumberContainer
            className="substance__meta__item"
            casRegistryNumbers={details?.casRegistryNumbers}
            substanceId={details?.id}
            databaseId={databaseId}
            resultName={resultName}
            name={details?.chemicalNames && details?.chemicalNames[0]}
            structure={molImage?.structure}
            inChiKey={details?.inChiKey}
            withCta
          />
        ) : (
          isCasNumberAllowed(databaseId) &&
          !isEmpty(details?.casRegistryNumbers) && (
            <SubstanceMetaItemComponent
              tooltip="CAS Number"
              content={isArray(details?.casRegistryNumbers) ? details?.casRegistryNumbers?.join(', ') : details?.casRegistryNumbers}
            />
          )
        )}
        {sort === 'SIMILARITY' && similarityPercentage >= 0 && (
          <div className="u-els-display-flex u-els-margin-left u-els-font-size-meta">
            <p className="u-els-margin-left-1o4">Similarity: {similarityPercentage}%</p>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.elsevier.support/reaxys/answer/how-is-the-substance-similarity-score-calculated">
              <sup>
                <Icon sprite="HelpOutlineCircle" color="secondary" size="xs" isTextFirst isDecorative>
                  This link explains how the similarity percentage is calculated
                </Icon>
              </sup>
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

SubstanceHeader.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default SubstanceHeader;
